import { useEffect, useState, type CSSProperties, type ReactElement, type SyntheticEvent } from 'react'

import { initials } from '@/lib/format'
import styles from './Avatar.module.css'

function cx(...values: Array<string | false | null | undefined>): string {
  return values.filter((value): value is string => typeof value === 'string' && value !== '').join(' ')
}

/**
 * A stable hue per identity. Recognising a colleague by the colour of their
 * avatar only works if the colour never moves, so it is derived from the id
 * rather than from a counter or from where they happen to sit in a list.
 *
 * Saturation and lightness are fixed in CSS; only the hue varies, which is why
 * the whole set stays as quiet as the rest of the palette.
 */
function hueFor(seed: string): number {
  let hash = 0

  for (let index = 0; index < seed.length; index += 1) {
    hash = (Math.imul(hash, 31) + seed.charCodeAt(index)) | 0
  }

  return Math.abs(hash) % 360
}

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg'
export type AvatarPresence = 'online' | 'offline'

export interface AvatarProps {
  /** Display name. Supplies the initials and, unless decorative, the label. */
  name: string
  /** Colour seed. Use the user id so a rename does not recolour the person. */
  id?: string
  src?: string | null
  size?: AvatarSize
  presence?: AvatarPresence | null
  /** Set when the name is already written next to the avatar. */
  decorative?: boolean
  className?: string
}

export function Avatar({
  name,
  id,
  src,
  size = 'md',
  presence = null,
  decorative = false,
  className,
}: AvatarProps): ReactElement {
  const seed = id !== undefined && id !== '' ? id : name
  const style = { '--avatar-hue': `${hueFor(seed)}` } as CSSProperties
  const imageSrc = typeof src === 'string' && src !== '' ? src : null
  const [imageFailed, setImageFailed] = useState(false)

  useEffect(() => {
    setImageFailed(false)
  }, [imageSrc])

  const showImage = imageSrc !== null && !imageFailed

  const onImageError = (event: SyntheticEvent<HTMLImageElement>): void => {
    event.currentTarget.removeAttribute('src')
    setImageFailed(true)
  }

  return (
    <span
      className={cx(styles.avatar, styles[size], className)}
      style={style}
      role={decorative ? undefined : 'img'}
      aria-label={decorative ? undefined : name}
      aria-hidden={decorative ? true : undefined}
    >
      {showImage ? (
        <img
          className={styles.image}
          src={imageSrc}
          alt=""
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer"
          onError={onImageError}
        />
      ) : (
        <span className={styles.initials} aria-hidden="true">
          {initials(name)}
        </span>
      )}
      {presence !== null ? (
        <span
          className={cx(styles.presence, presence === 'online' ? styles.online : styles.offline)}
          aria-hidden="true"
        />
      ) : null}
    </span>
  )
}
